import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Axios instance for dashboard-related API calls
 * - Base URL `${API_BASE_URL}/api/v1`
 */
// Create axios instance for dashboard API calls
const dashboardAPI = axios.create({
    baseURL: `${API_BASE_URL}/api/v1`,
    headers: {
        'Content-Type': 'application/json',
    },
    paramsSerializer: warehouseParamsSerializer,
});

/**
 * Request interceptor
 * - Attaches stored auth token when present. Includes a short debug log
 *   which can help during development. Remove or gate this log in prod.
 */
// Add request interceptor to include auth token
dashboardAPI.interceptors.request.use(
    (config) => {
        // Check for auth_token (matches your storage system) and also access_token as fallback
        const token = getAuthToken();

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            // Debug log to verify token is being sent (remove in production)
            // console.log('Sending Bearer token with dashboardAPI request:', token.substring(0, 20) + '...');
        } else {
            console.warn('No auth token found in storage for dashboardAPI request');
        }
        return applyWarehouseFilter(config);
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Response interceptor
 * - Clears local/session storage and redirects to login on 401 responses
 */
// Add response interceptor to handle auth errors
dashboardAPI.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid - clear storage and redirect to login
            localStorage.removeItem('auth_token');
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_data');
            sessionStorage.removeItem('auth_token');
            sessionStorage.removeItem('access_token');
            sessionStorage.removeItem('user_data');

            // Redirect to login page
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

/**
 * Normalize axios error objects into human-readable messages.
 * - Tries common server response shapes and falls back to generic text
 */
// Helper to extract backend error details from axios error

/**
 * Dashboard Service Class
 * Handles all API operations related to dashboard analytics
 */
class DashboardService {

    /**
     * Get main dashboard data
     * URL: /api/v1/data-analytics/dashboard
     * @param {Object} params - Query parameters (e.g. date range)
     * @returns {Promise<Object>} Dashboard data
     */
    async getDashboardData(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/dashboard', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get dashboard data error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get top selling products
     * URL: /api/v1/data-analytics/sales/top-products
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Top products data
     */
    async getSalesTopProducts(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/sales/top-products', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get sales top products error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get sales overview
     * URL: /api/v1/data-analytics/sales/overview
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Sales overview data
     */
    async getSalesOverview(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/sales/overview', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get sales overview error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get sales trend
     * URL: /api/v1/data-analytics/sales/trend
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Sales trend data
     */
    async getSalesTrend(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/sales/trend', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get sales trend error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get sales by category
     * URL: /api/v1/data-analytics/sales/by-category
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Sales by category data
     */
    async getSalesByCategory(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/sales/by-category', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get sales by category error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get sales by warehouse
     * URL: /api/v1/data-analytics/sales/by-warehouse
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Sales by warehouse data
     */
    async getSalesByWarehouse(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/sales/by-warehouse', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get sales by warehouse error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get sales order status
     * URL: /api/v1/data-analytics/sales/order-status
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Sales order status data
     */
    async getSalesOrderStatus(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/sales/order-status', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get sales order status error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }


    /**
     * Get delivery challans analytics
     * URL: /api/v1/data-analytics/delivery/challans
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Delivery challans data
     */
    async getDeliveryChallans(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/delivery/challans', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get delivery challans error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get delivery performance analytics
     * URL: /api/v1/data-analytics/delivery/performance
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Delivery performance data
     */
    async getDeliveryPerformance(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/delivery/performance', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get delivery performance error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get delivery personnel analytics
     * URL: /api/v1/data-analytics/delivery/personnel
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Delivery personnel data
     */
    async getDeliveryPersonnel(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/delivery/personnel', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get delivery personnel error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get purchase overview
     * URL: /api/v1/data-analytics/purchase/overview
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Purchase overview data
     */
    async getPurchaseOverview(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/purchase/overview', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get purchase overview error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get top suppliers
     * URL: /api/v1/data-analytics/purchase/top-suppliers
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Top suppliers data
     */
    async getPurchaseTopSuppliers(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/purchase/top-suppliers', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get purchase top suppliers error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get purchase trend
     * URL: /api/v1/data-analytics/purchase/trend
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Purchase trend data
     */
    async getPurchaseTrend(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/purchase/trend', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get purchase trend error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get warehouse stock analytics
     * URL: /api/v1/data-analytics/inventory/warehouse-stock
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Warehouse stock data
     */
    async getInventoryWarehouseStock(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/inventory/warehouse-stock', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get inventory warehouse stock error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get low stock analytics
     * URL: /api/v1/data-analytics/inventory/low-stock
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Low stock data
     */
    async getInventoryLowStock(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/inventory/low-stock', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get inventory low stock error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get top customers
     * URL: /api/v1/data-analytics/customers/top
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Top customers data
     */
    async getCustomersTop(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/customers/top', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get customers top error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get customers growth
     * URL: /api/v1/data-analytics/customers/growth
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Customers growth data
     */
    async getCustomersGrowth(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/customers/growth', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get customers growth error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get inquiries summary
     * URL: /api/v1/data-analytics/inquiries/summary
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Inquiries summary data
     */
    async getInquiriesSummary(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/inquiries/summary', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get inquiries summary error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get inquiries trend
     * URL: /api/v1/data-analytics/inquiries/trend
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Inquiries trend data
     */
    async getInquiriesTrend(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/inquiries/trend', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get inquiries trend error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get financial GST summary
     * URL: /api/v1/data-analytics/financial/gst-summary
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Financial GST summary data
     */
    async getFinancialGstSummary(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/financial/gst-summary', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get financial GST summary error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get financial TCS analytics
     * URL: /api/v1/data-analytics/financial/tcs
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Financial TCS data
     */
    async getFinancialTcs(params = {}) {
        try {
            const response = await dashboardAPI.get('/data-analytics/financial/tcs', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get financial TCS error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

    /**
     * Get financial revenue comparison
     * URL: /api/v1/data-analytics/financial/revenue-comparison
     * @param {Object} params - Query parameters (current_period, previous_period)
     * @returns {Promise<Object>} Financial revenue comparison data
     */
    async getFinancialRevenueComparison(params = { current_period: 'this_month', previous_period: 'last_month' }) {
        try {
            const response = await dashboardAPI.get('/data-analytics/financial/revenue-comparison', { params });
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error('Get financial revenue comparison error:', error.response?.data || error.message || error);
            return {
                success: false,
                error: extractApiError(error),
            };
        }
    }

}

// Export singleton instance
const dashboardService = new DashboardService();
export default dashboardService;
